import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  BouquetTemplateLineKind,
  Color,
  DefaultUom,
  Location,
  SubCategory,
} from "../api/types";
import { ItemPicker } from "../components/ItemPicker";
import { shopifyVariantLabel } from "../components/shopify/shopifyVariantLabel";
import { ColorChip } from "../components/ui/ColorChip";

const UOMS: DefaultUom[] = ["UNIT", "STEM", "CM", "ROLL"];

type Line = {
  kind: BouquetTemplateLineKind;
  itemId: string;
  subCategoryId: string;
  colorId: string;
  /** Acceptable colors for paper/ribbon lines. */
  colorIds: string[];
  quantity: string;
  uom: DefaultUom;
};

const newFlowerLine = (): Line => ({
  kind: "FLOWER_REQUIREMENT",
  itemId: "",
  subCategoryId: "",
  colorId: "",
  colorIds: [],
  quantity: "1",
  uom: "STEM",
});

const isPackagingKind = (kind: BouquetTemplateLineKind): boolean =>
  kind === "PAPER_REQUIREMENT" || kind === "RIBBON_REQUIREMENT";

/** Only an in-app path may be a return target — never another site. */
function safeReturnTo(value: string | null): string | null {
  return value && value.startsWith("/app/") && !value.startsWith("//") ? value : null;
}

export function BouquetTemplateNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // G-111 S4: "Create recipe from this product" arrives with the Shopify size
  // it is for; saving links the new recipe to it and goes back.
  const shopifyVariantId = searchParams.get("shopifyVariant");
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const [shopifyLabel, setShopifyLabel] = useState<string | null>(null);
  const [shopifyPhotoUsed, setShopifyPhotoUsed] = useState(false);
  const userPickedPhotoRef = useRef(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState(""); // "" = global (all locations)
  const [lines, setLines] = useState<Line[]>([newFlowerLine()]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.listColors().then(setColors).catch(() => setColors([]));
    void api.listSubCategories("FLOWER").then(setSubCategories).catch(() => setSubCategories([]));
    void api
      .listLocations()
      .then((rows) => setLocations(rows.filter((l) => l.isActive)))
      .catch(() => setLocations([]));
  }, []);

  useEffect(() => {
    if (!shopifyVariantId) return;
    let cancelled = false;
    void api
      .getShopifyProduct(shopifyVariantId)
      .then((variant) => {
        if (cancelled) return;
        const label = shopifyVariantLabel(variant);
        setShopifyLabel(label);
        setName((current) => current || label.slice(0, 120));
      })
      .catch(() => !cancelled && setShopifyLabel(null));
    void api.fetchShopifyProductImageFile(shopifyVariantId).then((file) => {
      // Never replace a photo the person already chose themselves.
      if (cancelled || !file || userPickedPhotoRef.current) return;
      setImageFile(file);
      setShopifyPhotoUsed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [shopifyVariantId]);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const setLine = (idx: number, patch: Partial<Line>) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addLine = () => setLines((ls) => [...ls, newFlowerLine()]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const parsed = lines.map((l) => {
        if (isPackagingKind(l.kind)) {
          if (l.colorIds.length === 0) {
            throw new Error(
              l.kind === "PAPER_REQUIREMENT"
                ? "Paper lines need at least one acceptable color"
                : "Ribbon lines need at least one acceptable color",
            );
          }
          return { kind: l.kind, colorIds: l.colorIds };
        }
        const q = Number(l.quantity);
        if (!Number.isFinite(q) || q <= 0) {
          throw new Error("Each flower or item line needs a positive quantity");
        }
        if (l.kind === "FLOWER_REQUIREMENT") {
          if (!l.subCategoryId) throw new Error("Flower lines need a flower type");
          return {
            kind: "FLOWER_REQUIREMENT" as const,
            subCategoryId: l.subCategoryId,
            colorId: l.colorId || undefined,
            quantity: q,
            uom: "STEM" as DefaultUom,
          };
        }
        if (!l.itemId) throw new Error("Exact item lines need an item");
        return {
          kind: "EXACT_ITEM" as const,
          itemId: l.itemId,
          quantity: q,
          uom: l.uom,
        };
      });
      if (!parsed.some((l) => l.kind === "FLOWER_REQUIREMENT" || l.kind === "EXACT_ITEM")) {
        throw new Error("Add at least one flower or exact-item line");
      }
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        locationId: locationId || undefined,
        lines: parsed,
      };
      const created = imageFile
        ? await api.createBouquetTemplateWithImage(payload, imageFile)
        : await api.createBouquetTemplate(payload);
      if (shopifyVariantId) {
        try {
          await api.setShopifyLink({
            shopifyVariantId,
            linkType: "BOUQUET_TEMPLATE",
            bouquetTemplateId: created.id,
          });
        } catch {
          // The recipe exists; saving again would duplicate it. Open the
          // picker for this product instead, where the new recipe is listed.
          navigate(`/app/shopify/products?variant=${encodeURIComponent(shopifyVariantId)}`);
          return;
        }
      }
      navigate(returnTo ?? "/app/bouquets/templates");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h2 className="page-title">New bouquet template</h2>
        <p className="page-subtitle">
          Save flower requirements plus optional exact paper, ribbon, toy, or packaging items.
        </p>
      </div>
      <form className="card" onSubmit={handleSubmit}>
        {error ? <div className="alert error full">{error}</div> : null}
        {shopifyVariantId ? (
          <div className="alert full">
            {shopifyLabel
              ? `For the Shopify product "${shopifyLabel}". Its name${
                  shopifyPhotoUsed ? " and photo are" : " is"
                } filled in; saving links this recipe to it.`
              : "Saving links this recipe to the Shopify product you came from."}
          </div>
        ) : null}
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="btName">Name *</label>
            <input
              id="btName"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field full">
            <label htmlFor="btDesc">Description</label>
            <input
              id="btDesc"
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="field full">
            <label htmlFor="btLocation">Availability</label>
            <select
              id="btLocation"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">Global — all locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                  {loc.code ? ` (${loc.code})` : ""}
                </option>
              ))}
            </select>
            <p className="field-hint">
              Global templates show at every shop. Pick a location to limit this
              template to that shop only.
            </p>
          </div>
          <div className="field full">
            <label htmlFor="btPhoto">Photo</label>
            <input
              id="btPhoto"
              className="field-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                userPickedPhotoRef.current = true;
                setShopifyPhotoUsed(false);
                setImageFile(e.target.files?.[0] ?? null);
              }}
            />
            <p className="field-hint">Optional. JPEG, PNG, or WebP, up to 2 MB.</p>
            {previewUrl ? (
              <div className="item-image-preview">
                <img src={previewUrl} alt="" />
              </div>
            ) : null}
          </div>
        </div>

        <div className="bt-lines">
          <span className="field-label-strong">Lines</span>
          {lines.map((line, idx) => {
            const packaging = isPackagingKind(line.kind);
            return (
              <div key={idx} className="bt-line">
                <div className="field bt-line__type">
                  <label>Line type</label>
                  <select
                    value={line.kind}
                    onChange={(e) => {
                      const kind = e.target.value as BouquetTemplateLineKind;
                      setLine(idx, {
                        kind,
                        itemId: "",
                        subCategoryId: "",
                        colorId: "",
                        colorIds: [],
                        uom:
                          kind === "FLOWER_REQUIREMENT"
                            ? "STEM"
                            : kind === "EXACT_ITEM"
                              ? "UNIT"
                              : "CM",
                      });
                    }}
                  >
                    <option value="FLOWER_REQUIREMENT">Flower</option>
                    <option value="EXACT_ITEM">Exact item</option>
                    <option value="PAPER_REQUIREMENT">Paper (by color)</option>
                    <option value="RIBBON_REQUIREMENT">Ribbon (by color)</option>
                  </select>
                </div>

                <div className="bt-line__main">
                  {line.kind === "FLOWER_REQUIREMENT" ? (
                    <>
                      <div className="field">
                        <label>Flower type</label>
                        <select
                          value={line.subCategoryId}
                          onChange={(e) =>
                            setLine(idx, { subCategoryId: e.target.value })
                          }
                        >
                          <option value="">Select type</option>
                          {subCategories.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label>Color</label>
                        <select
                          value={line.colorId}
                          onChange={(e) =>
                            setLine(idx, { colorId: e.target.value })
                          }
                        >
                          <option value="">Any color</option>
                          {colors.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : packaging ? (
                    <div className="field bt-line__colors">
                      <div className="bt-color-head">
                        <label>Acceptable colors</label>
                        {/* The rule this control encodes, said out loud: the
                            florist may use ANY of the ticked colours. */}
                        <span className="muted bt-color-hint">
                          {line.colorIds.length === 0
                            ? "Pick one or more — any of them will do"
                            : `${line.colorIds.length} selected · any of these will do`}
                        </span>
                      </div>
                      <div className="bt-color-chips">
                        {colors.length === 0 ? (
                          <span className="muted">No colors defined yet</span>
                        ) : (
                          colors.map((c) => {
                            const selected = line.colorIds.includes(c.id);
                            return (
                              <ColorChip
                                key={c.id}
                                name={c.name}
                                hex={c.hex}
                                selected={selected}
                                onToggle={() =>
                                  setLine(idx, {
                                    colorIds: selected
                                      ? line.colorIds.filter((cid) => cid !== c.id)
                                      : [...line.colorIds, c.id],
                                  })
                                }
                              />
                            );
                          })
                        )}
                      </div>
                    </div>
                  ) : (
                    <ItemPicker
                      label={`Item ${idx + 1}`}
                      value={line.itemId}
                      onChange={(v) => setLine(idx, { itemId: v })}
                    />
                  )}
                </div>

                {!packaging ? (
                  <>
                    <div className="field bt-line__qty">
                      <label>Qty</label>
                      <input
                        type="number"
                        min={0.001}
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) =>
                          setLine(idx, { quantity: e.target.value })
                        }
                      />
                    </div>
                    <div className="field bt-line__uom">
                      <label>UoM</label>
                      <select
                        value={line.kind === "FLOWER_REQUIREMENT" ? "STEM" : line.uom}
                        disabled={line.kind === "FLOWER_REQUIREMENT"}
                        onChange={(e) =>
                          setLine(idx, { uom: e.target.value as DefaultUom })
                        }
                      >
                        {UOMS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : null}

                <div className="bt-line__remove">
                  <button
                    type="button"
                    className="button plain"
                    onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                    disabled={lines.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="button-row">
          <button type="button" className="button tinted" onClick={addLine}>
            Add line
          </button>
          <button className="button" type="submit" disabled={submitting || !name.trim()}>
            {submitting ? "Saving..." : "Save template"}
          </button>
        </div>
      </form>
    </section>
  );
}
